/**
 * Camera controls — 1:1 port of ui/src/viewer/controls.js from the
 * desktop viewer.
 *
 * Left drag   = Pan along ground plane
 * Middle drag = Orbit around target
 * Right drag  = Dolly (zoom drag) — handled by OrbitControls default
 * Scroll      = Zoom in/out toward cursor
 * Z always up (no roll)
 *
 * The "ground-snap" wheel debounce (lines 56-67 in controls.js) keeps
 * `target` glued to whatever surface is under the screen centre after
 * each wheel burst, so panSpeed (which scales with camera-target
 * distance) stays sensible at every zoom level.
 *
 * Lite-specific tweak: 3DRoad's controls.js takes `pickSurfaceFn` as a
 * constructor argument; we expose `setPickFn` instead because Lite
 * creates the Picking instance only after the tileset loads.
 */

import { Camera, MOUSE, Vector3 } from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'

export type PickFn = (clientX: number, clientY: number) => Vector3 | null

export class Controls {
  readonly orbit: OrbitControls
  private dom: HTMLElement
  private pickFn: PickFn | null = null
  private wheelDebounce: number | null = null
  private onChangeBound: () => void
  private onWheelBound: () => void

  constructor(camera: Camera, dom: HTMLElement) {
    this.dom = dom
    const controls = new OrbitControls(camera, dom)
    this.orbit = controls

    // Mouse mapping — right click reserved for context menus
    controls.mouseButtons = {
      LEFT: MOUSE.PAN,
      MIDDLE: MOUSE.ROTATE,
      RIGHT: null as unknown as MOUSE,
    }
    controls.enableZoom = true

    // Zoom settings — scroll wheel zooms into whatever is under the cursor,
    // not the fixed scene centre.
    controls.zoomToCursor = true
    controls.zoomSpeed = 1.4

    // Pan speed (left mouse drag)
    controls.panSpeed = 1.4

    // Smooth motion
    controls.enableDamping = true
    controls.dampingFactor = 0.25

    // Pan along ground plane — left drag moves forward/backward, not up/down
    controls.screenSpacePanning = false

    // Allow near-vertical views
    controls.maxPolarAngle = Math.PI * 0.98
    controls.minPolarAngle = Math.PI * 0.02

    // ── Snap orbit target back to the ground after zooming ──────
    this.onWheelBound = () => this.scheduleGroundSnap()
    dom.addEventListener('wheel', this.onWheelBound, { passive: true })

    // Enforce Z-up after any rotation
    this.onChangeBound = () => {
      camera.up.set(0, 0, 1)
    }
    controls.addEventListener('change', this.onChangeBound)
  }

  /** Plug in a surface picker for the wheel ground-snap. */
  setPickFn(fn: PickFn | null): void {
    this.pickFn = fn
  }

  update(): void {
    this.orbit.update()
  }

  dispose(): void {
    this.orbit.removeEventListener('change', this.onChangeBound)
    this.dom.removeEventListener('wheel', this.onWheelBound)
    if (this.wheelDebounce != null) {
      window.clearTimeout(this.wheelDebounce)
      this.wheelDebounce = null
    }
    this.orbit.dispose()
  }

  private scheduleGroundSnap(): void {
    if (this.wheelDebounce != null) {
      window.clearTimeout(this.wheelDebounce)
    }
    this.wheelDebounce = window.setTimeout(() => {
      this.wheelDebounce = null
      if (!this.pickFn) return
      const rect = this.dom.getBoundingClientRect()
      const hit = this.pickFn(
        rect.left + rect.width / 2,
        rect.top + rect.height / 2,
      )
      if (!hit) return
      this.orbit.target.set(hit.x, hit.y, hit.z)
      this.orbit.update()
    }, 120)
  }
}
